import * as THREE from 'three'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise } from '../shaderChunks'

/**
 * Plasma orb: a glowing energy sphere raymarched volumetrically. Swirling fbm
 * filaments fill a fuzzy sphere; it breathes with the bass, brightens with the
 * level and flares on the beat. Colour shifts by frequency band. Emissive
 * (additive) so the bloom makes it glow.
 */
export class Plasma extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const half = new THREE.Vector3(4.4, 4.4, 4.4)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime, uEnergyTime, uBass, uMid, uTreble, uLevel, uBeat, uBeatPulse, uReactivity, uBassPunch, uHue;
        uniform vec3 uBoxHalf;
        varying vec3 vWorld;
        ${snoise}
        ${hsv2rgb}
        float fbm(vec3 p) {
          float s = 0.0, a = 0.5;
          for (int i = 0; i < 3; i++) { s += a * snoise(p); p *= 2.03; a *= 0.5; }
          return s;
        }
        vec3 rotY(vec3 p, float a) {
          float c = cos(a), s = sin(a);
          return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
        }
        float density(vec3 p, out float fil) {
          float rr = length(p);
          // Strong breathing: the bass swells the orb, the beat punches it.
          float radius = 2.0 + uBass * uBassPunch * 1.1 * uReactivity + uBeatPulse * 0.9 * uReactivity;
          // Swirl speeds up with the energy.
          vec3 q = rotY(p, uEnergyTime * 0.4);
          float f = fbm(q * 1.0 + vec3(0.0, 0.0, uEnergyTime * 0.5));
          fil = abs(f);
          float shell = smoothstep(radius + 0.6, radius - 1.1, rr);
          float d = shell * (0.22 + fil * 1.0);
          d *= 0.7 + uLevel * 0.9 + uBeatPulse * 0.9 * uReactivity;
          return clamp(d, 0.0, 1.0);
        }
        void main() {
          vec3 ro = cameraPosition;
          vec3 rd = normalize(vWorld - ro);
          vec3 t1 = (-uBoxHalf - ro) / rd;
          vec3 t2 = (uBoxHalf - ro) / rd;
          vec3 tmin = min(t1, t2), tmax = max(t1, t2);
          float tn = max(max(tmin.x, tmin.y), tmin.z);
          float tf = min(min(tmax.x, tmax.y), tmax.z);
          tn = max(tn, 0.0);
          if (tn >= tf) discard;
          const int STEPS = 44;
          float dt = (tf - tn) / float(STEPS);
          float t = tn + dt * 0.5;
          vec3 acc = vec3(0.0);
          float bandShift = (uTreble - uBass) * 0.22 * uReactivity;
          float glow = 1.8 + uLevel * 1.4 + uBeatPulse * 1.8 * uReactivity;   // flares on the beat
          for (int i = 0; i < STEPS; i++) {
            vec3 p = ro + rd * t;
            float fil;
            float dens = density(p, fil);
            if (dens > 0.001) {
              float hue = fract(uHue + fil * 0.18 + bandShift + length(p) * 0.04);
              vec3 col = hsv2rgb(vec3(hue, 0.85, 1.0));
              acc += col * dens * dt * glow;
            }
            t += dt;
          }
          if (max(acc.r, max(acc.g, acc.b)) < 0.01) discard;
          gl_FragColor = vec4(acc, 1.0);
        }
      `,
    })
    this.material.uniforms.uBoxHalf = { value: half }
    this.object3D = new THREE.Mesh(new THREE.BoxGeometry(half.x * 2, half.y * 2, half.z * 2), this.material)
    this.object3D.frustumCulled = false
  }

  rebuild(): void {}
}
